import { DestroyRef, Injectable, WritableSignal, effect, inject, signal, computed, Signal } from "@angular/core";
import { AgentKitService } from "./agent-kit.service";
import type { AbstractAgent, Message } from "@ag-ui/client";
import { DEFAULT_AGENT_ID } from "@copilotkit/shared";
import { ProxiedCopilotRuntimeAgent } from "@copilotkit/core";

export class AgentStore {
  readonly #subscription?: {
    unsubscribe: () => void;
  };

  readonly #isRunning = signal<boolean>(false);

  readonly #messages = signal<Message[]>([]);

  readonly #state = signal<any>(undefined);

  readonly #runError = signal<string | null>(null);

  readonly agent: AbstractAgent;

  readonly isRunning = this.#isRunning.asReadonly();

  readonly messages = this.#messages.asReadonly();

  readonly state = this.#state.asReadonly();

  readonly runError = this.#runError.asReadonly();

  readonly #agentId: string;

  constructor(abstractAgent: AbstractAgent) {
    this.agent = abstractAgent;
    this.#agentId = (abstractAgent as any).agentId ?? "unknown";

    this.#subscription = abstractAgent.subscribe({
      // Guard against late TOOL_CALL_ARGS deltas arriving after a MESSAGES_SNAPSHOT has
      // already placed the complete JSON into toolCall.function.arguments.
      // @ag-ui/client appends the delta unconditionally unless stopPropagation is returned,
      // which causes corruption like: {"lon":-122.4783,"lat":37.8267}-
      onToolCallArgsEvent: ({ toolCallBuffer, event }) => {
        try {
          JSON.parse(toolCallBuffer);
          // Buffer is already valid JSON — a late delta would corrupt it.
          console.debug(
            `[AgentStore] Blocked late TOOL_CALL_ARGS delta` +
              ` toolCallId=${(event as any).toolCallId}` +
              ` delta=${JSON.stringify((event as any).delta)}` +
              ` bufferLen=${toolCallBuffer.length}`,
          );
          return { stopPropagation: true } as any;
        } catch {
          return undefined;
        }
      },
      onMessagesChanged: () => {
        this.#messages.set([...abstractAgent.messages]);
      },
      onStateChanged: () => {
        this.#state.set(abstractAgent.state);
      },
      onRunInitialized: () => {
        this.#isRunning.set(true);
        this.#runError.set(null);
      },
      onRunFinalized: () => {
        this.#isRunning.set(false);
      },
      onRunFailed: ({ error }) => {
        console.error(`[AgentStore] run failed:`, error?.message);
        this.#isRunning.set(false);
        this.#runError.set(error?.message ?? "The agent run failed. Please try again.");
      },
    });
  }

  /** Replace all messages in the store and on the underlying agent. */
  seedMessages(messages: Message[], threadId?: string, state?: unknown): void {
    if (threadId !== undefined) (this.agent as any).threadId = threadId;
    if (state !== undefined) {
      (this.agent as any).state = state;
      this.#state.set(state);
    }
    this.agent.messages = [...messages];
    this.#messages.set([...messages]);
  }

  clearMessages(): void {
    (this.agent as any).state = undefined;
    this.#state.set(undefined);
    this.seedMessages([]);
  }

  teardown(): void {
    this.#subscription?.unsubscribe();
  }
}

/**
 * Root-scoped factory that owns one long-lived AgentStore per agentId.
 * Stores are never torn down when a chat component is destroyed — they survive
 * panel close/open, view switches (bubble ↔ toolbar), and component recreations.
 * Only explicit calls to clearMessages() or a full app shutdown destroy them.
 */
@Injectable({ providedIn: "root" })
export class AgentFactory {
  readonly #agentKit = inject(AgentKitService);

  readonly #destroyRef = inject(DestroyRef);

  readonly #stores = new Map<string, AgentStore>();

  readonly #signals = new Map<string, WritableSignal<AgentStore>>();

  constructor() {
    // Sync auth headers on all live stores and rebuild if the abstract-agent reference
    // changes (e.g. runtime reconnect) while preserving current message history.
    effect(() => {
      const headers = this.#agentKit.headers();
      this.#agentKit.agents(); // track registrations so the effect re-runs on agent changes

      this.#signals.forEach((sig, id) => {
        const currentStore = this.#stores.get(id)!;
        const newAbstractAgent = this.#agentKit.getAgent(id);

        if (newAbstractAgent && currentStore.agent !== newAbstractAgent) {
          const savedMessages = currentStore.messages();
          const savedThreadId = (currentStore.agent as any).threadId as string | undefined;
          currentStore.teardown();
          (newAbstractAgent as any).headers = { ...headers };
          const newStore = new AgentStore(newAbstractAgent);
          if (savedMessages.length > 0) newStore.seedMessages(savedMessages, savedThreadId);
          this.#stores.set(id, newStore);
          sig.set(newStore);
        } else {
          (currentStore.agent as any).headers = { ...headers };
        }
      });
    });

    this.#destroyRef.onDestroy(() => {
      this.#stores.forEach((s) => s.teardown());
      this.#stores.clear();
      this.#signals.clear();
    });
  }

  /** Returns a stable computed signal shared by all consumers for the same agentId. */
  getSharedStoreSignal(agentId: Signal<string | undefined>): Signal<AgentStore> {
    return computed(() => this.#getOrCreateSignal(agentId() ?? DEFAULT_AGENT_ID)());
  }

  /** Clear message history for the given agent (call before starting a new chat). */
  clearMessages(agentId: string): void {
    this.#stores.get(agentId)?.clearMessages();
  }

  /** Seed an agent's store with messages, threadId and optionally the full LangGraph state snapshot. */
  seedForThread(agentId: string, messages: Message[], threadId: string, state?: unknown): void {
    this.#getOrCreateSignal(agentId);
    this.#stores.get(agentId)!.seedMessages(messages, threadId, state);
  }

  #getOrCreateSignal(agentId: string): WritableSignal<AgentStore> {
    if (this.#signals.has(agentId)) return this.#signals.get(agentId)!;

    const headers = this.#agentKit.headers();
    const runtimeUrl = this.#agentKit.runtimeUrl();
    const runtimeTransport = this.#agentKit.runtimeTransport();
    const abstractAgent = this.#agentKit.getAgent(agentId);
    const debug = this.#agentKit.config.debug;

    const agent =
      abstractAgent ??
      new ProxiedCopilotRuntimeAgent({
        runtimeUrl,
        agentId,
        transport: runtimeTransport,
        debug,
      });
    (agent as any).headers = { ...headers };

    const store = new AgentStore(agent);
    this.#stores.set(agentId, store);
    const sig = signal<AgentStore>(store);
    this.#signals.set(agentId, sig);
    return sig;
  }
}

export function injectAgentStore(agentId: string | Signal<string | undefined>): Signal<AgentStore> {
  const agentFactory = inject(AgentFactory);
  const agentIdSignal = typeof agentId === "function" ? agentId : computed(() => agentId);
  return agentFactory.getSharedStoreSignal(agentIdSignal);
}
